import { makeStyles } from '@material-ui/styles'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import { prop } from 'ramda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import ApiClient from 'api-client/ApiClient'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { type RootState, useAppSelector } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Dropdown from 'core/elements/dropdown'
import DropdownFieldWithComingSoon from 'core/elements/dropdown/DropdownFieldWithComingSoon'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import VirtualMachineFlavorsTable from 'k8s/components/cluster/deployment/form-components/VirtualMachineFlavorsTable'
import { listKaapiClusters, updateNodePools } from 'k8s/components/kaapi/clusters/actions'
import DataKeys from 'k8s/DataKeys'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import { filterFlavorsBasedOnSourceType } from '../cluster/deployment/form-components/helpers'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { listKaapiKubeadmControlPlanes } from '../kaapi/kubeadm-control-plane/actions'
import { kaapiKubeadmControlPlanesSelector } from '../kaapi/kubeadm-control-plane/selectors'

const { kubernetes } = ApiClient.getInstance()

export enum NodeUpdateStrategyTypes {
  Count = 'count',
  Percentage = 'percent',
}

const rollingUpdateTypeOptions = [
  { label: 'Count', value: NodeUpdateStrategyTypes.Count },
  { label: 'Percentage', value: NodeUpdateStrategyTypes.Percentage },
]

const convertToPercentageStr = (value: string | number) => {
  if (!value) return value
  return value.toString() + '%'
}

interface Props {
  rows: any[]
  clusterName: string
  onClose: () => void
}

export default function EditMasterNodeModal({
  rows: [masterNodeGroup],
  clusterName,
  onClose,
}: Props) {
  const { activeKaapiTenant: namespace } = useSelector<RootState, SessionState>(
    prop(sessionStoreKey),
  )
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()
  const { loading: loadingKaapiClusters, reload: reloadKaapiClusters } =
    useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = useMemo(
    () => clusters?.find((c) => c?.metadata?.name === clusterName),
    [clusters, clusterName],
  )
  const { loading: loadingKubeadmControlPlane, reload: reloadKubeadmControlPlane } = useListAction(
    listKaapiKubeadmControlPlanes,
  )
  const kubeadmControlPlanes = useAppSelector(kaapiKubeadmControlPlanesSelector)
  const kubeadmControlPlane = useMemo(() => {
    return kubeadmControlPlanes?.find((cp) => cp?.metadata?.name === clusterName)
  }, [kubeadmControlPlanes, clusterName])

  const isMaxSurgePercent = useMemo(() => {
    const maxSurge = kubeadmControlPlane?.spec?.rolloutStrategy?.rollingUpdate?.maxSurge
    return typeof maxSurge === 'string' && maxSurge.includes('%')
  }, [kubeadmControlPlane])

  const maxSurgeValue = useMemo(() => {
    return kubeadmControlPlane?.spec?.rolloutStrategy?.rollingUpdate?.maxSurge
  }, [kubeadmControlPlane])

  const { loading: loadingNetworks } = useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)
  // Filter networks by tenant
  const networks = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  const defaultParams = useMemo(
    () => ({
      replicas: Number(kubeadmControlPlane?.spec?.replicas) || 1,
      flavor: kubeadmControlPlane?.openStackMachineTemplate?.spec?.template?.spec?.flavor ?? '',
      maxSurgeType: isMaxSurgePercent
        ? NodeUpdateStrategyTypes.Percentage
        : NodeUpdateStrategyTypes.Count,
      maxSurgeValue: isMaxSurgePercent
        ? parseFloat(String(maxSurgeValue)?.replace('%', ''))
        : typeof maxSurgeValue === 'number'
          ? maxSurgeValue
          : null,
      network:
        kubeadmControlPlane?.openStackMachineTemplate?.spec?.template?.spec?.ports?.[0]?.network
          ?.id ?? '',
      subnet:
        kubeadmControlPlane?.openStackMachineTemplate?.spec?.template?.spec?.ports?.[0]
          ?.fixedIPs?.[0]?.subnet?.id ?? '',
    }),
    [kubeadmControlPlane, isMaxSurgePercent, maxSurgeValue],
  )

  // FR-018: Read-only; same source as before (control-plane failure domains)
  const virtualizedCluster = useMemo(() => {
    const failureDomains = cluster?.status?.failureDomains ?? {}
    return (
      Object.keys(failureDomains).find((key) => failureDomains[key]?.controlPlane === true) ?? ''
    )
  }, [cluster])

  const [maxSurgeType, setMaxSurgeType] = useState(
    isMaxSurgePercent ? NodeUpdateStrategyTypes.Percentage : NodeUpdateStrategyTypes.Count,
  )
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [replicasError, setReplicasError] = useState('')
  const [maxSurgeError, setMaxSurgeError] = useState('')

  const {
    progressDetails,
    updateProgress,
    resetProgress: resetClusterProgress,
  } = useClusterProgress({ initLabel: 'Updating Cluster' })

  const cancelledRef = useRef(false)

  const { update: updateMasterNodeGroup, updating, error, reset } = useUpdateAction(updateNodePools)

  const { loading: loadingFlavors } = useListAction(listFlavors)
  const allFlavors = useAppSelector(flavorsSelector)

  const networkOptions = useMemo(
    () =>
      networks?.map((network) => ({
        label: network?.name,
        value: network?.id,
      })),
    [networks],
  )

  const subnetOptionsByNetworkId = useMemo(
    () =>
      networks.reduce(
        (acc, network) => {
          acc[network?.id] = network?.subnetDetails?.map((subnet) => ({
            label: `${subnet?.name}: ${subnet?.cidr}`,
            value: subnet?.id,
          }))
          return acc
        },
        {} as Record<string, { label: string; value: string }[]>,
      ),
    [networks],
  )

  const filteredFlavors = useMemo(() => {
    const supported = allFlavors?.filter((f) => f?.vcpus > 0 && f?.ram > 0) ?? []
    const sortedFlavors = [...supported].sort((a, b) => b.vcpus - a.vcpus || b.ram - a.ram)
    return filterFlavorsBasedOnSourceType(
      sortedFlavors,
      params?.flavor?.includes('vol') ? 'volume' : 'image',
    )
  }, [allFlavors])

  const handleFlavorTableChange = useCallback(
    (value: { flavor?: string; replicas?: number }) => {
      updateParams(value)
    },
    [updateParams],
  )

  const classes = useStyles()

  const validate = useCallback(() => {
    let valid = true
    // FR-002: odd replicas only
    if (params.replicas % 2 === 0) {
      setReplicasError('Replicas must be an odd number (eg - 1, 3, 5, 7)')
      valid = false
    } else {
      setReplicasError('')
    }
    // Will be required in next release
    // // FR-005: maxSurge range
    // if (maxSurgeType === NodeUpdateStrategyTypes.Percentage) {
    //   if (params.maxSurgeValue < 0 || params.maxSurgeValue > 100) {
    //     setMaxSurgeError('Percentage must be between 0 and 100')
    //     valid = false
    //   } else {
    //     setMaxSurgeError('')
    //   }
    // } else {
    //   if (params.maxSurgeValue < 0 || params.maxSurgeValue > params.replicas) {
    //     setMaxSurgeError(`Count must be between 0 and ${params.replicas}`)
    //     valid = false
    //   } else {
    //     setMaxSurgeError('')
    //   }
    // }
    return valid
  }, [params, maxSurgeType])

  const submitForm = useCallback(async () => {
    if (!validate()) return
    setShowProgress(true)
    setLoading(true)
    updateProgress(ProgressStep.Init, ProgressStatus.Success)

    const masterNodeIndex = cluster?.spec?.topology.variables?.findIndex(
      (item) => item?.name === 'controlPlane',
    )
    // Network and Subnet operations deferred (marked as "Coming Soon") - keep networkIndex for future release
    const networkIndex = cluster?.spec?.topology.variables?.findIndex(
      (item) => item?.name === 'network',
    )

    const body: any[] = [
      {
        op: 'replace',
        path: '/spec/topology/controlPlane/replicas',
        value: Number(params.replicas),
      },
      ...(masterNodeIndex !== -1
        ? [
            {
              op: 'replace',
              path: `/spec/topology/variables/${masterNodeIndex}/value/flavor`,
              value: params.flavor,
            },
          ]
        : []),
      // Network and Subnet operations deferred (marked as "Coming Soon")
      // Will be required in next release
      // ...(networkIndex !== -1
      //   ? [
      //       {
      //         op: 'replace',
      //         path: `/spec/topology/variables/${networkIndex}/value`,
      //         value: { networkID: params.network, subnetID: params.subnet },
      //       },
      //     ]
      //   : []),
      // maxSurge field validation deferred - will be required in next release
      // {
      //   op: 'replace',
      //   path: '/spec/topology/controlPlane/variables/maxSurge',
      //   value:
      //     maxSurgeType === NodeUpdateStrategyTypes.Percentage
      //       ? convertToPercentageStr(params.maxSurgeValue)
      //       : params.maxSurgeValue,
      // },
    ]

    try {
      const { success } = await updateMasterNodeGroup({
        namespace,
        body,
        clusterName,
      })
      if (!success) {
        setLoading(false)
        setErrorMessage({ message: 'Failed to update control plane node group' })
        updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
        return
      }

      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
      const initialGeneration = masterNodeGroup?.metadata?.generation

      let isUpdated = false
      const maxAttempts = 60
      let attempts = 0

      while (!isUpdated && attempts < maxAttempts) {
        if (cancelledRef.current) break
        attempts++
        const clusters = await kubernetes.getAllCustomResources(DataKeys.KaapiClusters)
        const updatedCluster = clusters?.find((c: any) => c?.metadata?.name === clusterName)
        if (updatedCluster?.metadata?.generation !== initialGeneration) {
          isUpdated = true
          updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
          setIsProgressComplete(true)
          setLoading(false)
        } else {
          await waitSeconds(5)
        }
      }

      if (!isUpdated) {
        throw new Error('Control plane update is taking too long')
      }
    } catch (err) {
      setLoading(false)
      setErrorMessage(err || { message: 'Failed to update control plane node group' })
      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
    }
  }, [params, maxSurgeType, validate, namespace, clusterName, masterNodeGroup, cluster])

  const handleClose = useCallback(() => {
    cancelledRef.current = true
    setParams(defaultParams)
    reset()
    setIsProgressComplete(false)
    setShowProgress(false)
    setReplicasError('')
    setMaxSurgeError('')
    reloadKaapiClusters(true)
    reloadKubeadmControlPlane(true) // addDependency ensures templates reload too
    onClose()
  }, [setParams, defaultParams, reset, onClose])

  const resetProgress = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    resetClusterProgress()
    setErrorMessage(null)
  }, [resetClusterProgress])

  const submitting = updating || loading
  const editError = error || errorMessage

  return (
    <ModalForm
      open
      title="Edit Control Plane Nodes"
      onSubmit={showProgress || isProgressComplete ? handleClose : submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={editError}
      submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Update'}
      disableSubmit={submitting}
      maxWidth={820}
      onBackButtonClick={resetProgress}
      showBackButton={!!(editError?.message && showProgress)}
      data-testid="edit-master-node-group-modal"
      loading={
        loadingKubeadmControlPlane || loadingFlavors || loadingKaapiClusters || loadingNetworks
      }
    >
      {!showProgress && !isProgressComplete ? (
        <div className={classes.fields}>
          <div>
            <VirtualMachineFlavorsTable
              data={filteredFlavors}
              selectedVmFlavor={params.flavor}
              selectedVmQuantity={Number(params.replicas)}
              onChange={handleFlavorTableChange}
              loadingVmFlavors={loadingFlavors}
              isMasterNodePool
              data-testid="vm-flavors-table"
            />
            {replicasError && <ErrorMessage>{replicasError}</ErrorMessage>}
          </div>

          {/* Will be required in next release  */}
          {/* <DropdownField
            DropdownComponent={Dropdown}
            id="nodesUpdateStrategy"
            label="Strategy"
            value="RollingUpdate"
            disabled
            items={[{ label: 'Rolling Update', value: 'RollingUpdate' }]}
            data-testid="update-strategy-dropdown"
          /> */}

          <div>
            <DropdownField
              id="master-network-field"
              label="Network"
              DropdownComponent={DropdownFieldWithComingSoon}
              items={networkOptions}
              value={params.network || ''}
              onChange={getParamsUpdater('network')}
              info="The network should be able to communicate with both control plane nodes as well as worker nodes."
              required
              comingSoon
              data-testid="network-dropdown"
            />
          </div>
          <div>
            <DropdownField
              id="master-subnet-field"
              label="Subnet"
              DropdownComponent={DropdownFieldWithComingSoon}
              items={subnetOptionsByNetworkId[params?.network] || []}
              value={params.subnet || ''}
              onChange={getParamsUpdater('subnet')}
              disabled={!params.network}
              required={!!params.network}
              comingSoon
              data-testid="subnet-dropdown"
            />
          </div>
          {/* FR-018: read-only, same look as the create-form field. Workers' failureDomain
              is also fixed after create, so editing only the CP would split them. */}
          <DropdownField
            id="master-virtualized-cluster-field"
            label="Virtualized Cluster"
            DropdownComponent={Dropdown}
            value={virtualizedCluster}
            items={
              virtualizedCluster ? [{ label: virtualizedCluster, value: virtualizedCluster }] : []
            }
            disabled
            info="The virtualized cluster the control plane nodes run on. Set at cluster creation."
            data-testid="virtualized-cluster-dropdown"
          />
          {/* Will be required in next release */}
          {/* <DropdownField
            DropdownComponent={Dropdown}
            id="maxSurgeType"
            label="Max Surge Type"
            value={maxSurgeType}
            items={rollingUpdateTypeOptions}
            onChange={(value) => setMaxSurgeType(value)}
            data-testid="max-surge-type-dropdown"
          />

          <div>
            <TextField
              crossOrigin={undefined}
              id="maxSurgeValue"
              label="Max Surge Value"
              value={params.maxSurgeValue}
              onChange={(value: string) =>
                updateParams({ maxSurgeValue: value === '' ? null : parseFloat(value) })
              }
              data-testid="max-surge-value"
              type="number"
              required
              min={1}
              icon={isMaxSurgePercent ? 'percent' : ''}
              iconProps={{ placement: 'end' }}
              info={
                <div>
                  Maximum number/percentage of nodes that can be scaled up above the desired Node
                  Group replica count.
                  <br /> Applicable values:
                  <br /> Minimum = 1<br /> Maximum = Any number / percentage
                  <br /> Default = 0
                </div>
              }
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            {maxSurgeError && <ErrorMessage>{maxSurgeError}</ErrorMessage>}
          </div> */}
        </div>
      ) : (
        <StepProgress
          progressDetails={progressDetails}
          title="Control plane is updating. Please wait and do not close this dialog."
        />
      )}
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gridGap: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
}))
